import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Package, Truck, MapPin, DollarSign, Calendar, 
  CheckCircle, Send, Building2, ArrowRight, Phone, Mail,
  Shield, Clock, Users, Zap
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import PlatformDisclaimer from '@/components/PlatformDisclaimer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/0ee8c733-9ebe-4e41-9af6-e5aa20e6c9c1/images/664a6582c52f759cf8aa9c469d5205aa85a496bab31400c484f1532a982ea379.png";

const LoadPostingPortal = () => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reference, setReference] = useState('');
  const [userType, setUserType] = useState('broker'); // broker, carrier, shipper
  
  const [formData, setFormData] = useState({
    // Broker/Shipper Info
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    mc_number: '',
    business_type: 'broker', // broker, carrier, shipper, manufacturer
    
    // Pickup
    pickup_city: '',
    pickup_state: '',
    pickup_zip: '',
    pickup_date: '',
    
    // Delivery
    delivery_city: '',
    delivery_state: '',
    delivery_zip: '',
    delivery_date: '',
    
    // Load Details
    equipment_type: 'flatbed',
    weight_lbs: '',
    commodity: '',
    special_requirements: '',
    
    // Rate
    rate_type: 'flat',
    rate_amount: '',
    rate_notes: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: name.includes('state') ? value.toUpperCase() : value });
  };

  const submitLoad = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        weight_lbs: formData.weight_lbs ? parseInt(formData.weight_lbs) : null,
        rate_amount: formData.rate_amount ? parseFloat(formData.rate_amount) : null
      };
      
      const res = await axios.post(`${API}/public/post-load`, payload);
      setReference(res.data.reference);
      setSubmitted(true);
      toast.success('Load posted successfully!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to post load. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    if (step === 1) {
      if (!formData.company_name || !formData.email || !formData.phone) {
        toast.error('Please fill in company name, email, and phone');
        return;
      }
      // MC required for brokers and carriers, optional for shippers
      if (userType !== 'shipper' && !formData.mc_number) {
        toast.error('MC number is required for brokers and carriers');
        return;
      }
    }
    if (step === 2 && (!formData.pickup_city || !formData.pickup_state || !formData.pickup_date)) {
      toast.error('Please fill in pickup city, state, and date');
      return;
    }
    if (step === 3 && (!formData.delivery_city || !formData.delivery_state)) {
      toast.error('Please fill in delivery city and state');
      return;
    }
    setStep(step + 1);
  };

  // Success Screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Toaster position="top-right" richColors />
        
        {/* Header */}
        <header className="p-4 border-b border-slate-700">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <img src={LOGO_URL} alt="Tempus Dispatch" className="h-12" />
          </div>
        </header>

        <div className="max-w-2xl mx-auto p-6 mt-12">
          <Card className="bg-slate-800/50 border-green-500/50">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">Load Posted Successfully!</h1>
              <p className="text-slate-400 mb-6">
                Your load has been submitted to our network of qualified carriers.
              </p>
              
              <div className="bg-slate-900 rounded-lg p-6 mb-6">
                <p className="text-sm text-slate-400 mb-2">Your Reference Number</p>
                <p className="text-4xl font-mono font-bold text-green-400">{reference}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-slate-900 rounded-lg">
                  <Clock className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">Response Time</p>
                  <p className="text-white font-bold">1-2 Hours</p>
                </div>
                <div className="text-center p-4 bg-slate-900 rounded-lg">
                  <Users className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">Carrier Network</p>
                  <p className="text-white font-bold">733K+ Drivers</p>
                </div>
                <div className="text-center p-4 bg-slate-900 rounded-lg">
                  <Shield className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">Coverage</p>
                  <p className="text-white font-bold">All 48 States</p>
                </div>
              </div>
              
              <p className="text-slate-400 text-sm mb-6">
                We've sent a confirmation to <strong className="text-white">{formData.email}</strong>
              </p>
              
              <Button 
                onClick={() => { setSubmitted(false); setStep(1); setFormData({...formData, pickup_city: '', delivery_city: ''}); }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Post Another Load
              </Button>
            </CardContent>
          </Card>
        </div>
        <PlatformDisclaimer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="p-4 border-b border-slate-700">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="Tempus Dispatch" className="h-12" />
          <div className="flex items-center gap-4 text-sm text-slate-400">
            <a href="tel:+1234567890" className="flex items-center gap-1 hover:text-white">
              <Phone className="w-4 h-4" /> (123) 456-7890
            </a>
            <a href="mailto:loads@tempusdispatch.com" className="flex items-center gap-1 hover:text-white">
              <Mail className="w-4 h-4" /> loads@tempusdispatch.com
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 py-12 border-b border-slate-700">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h1 className="text-4xl font-bold text-white mb-4">Post Your Load in 60 Seconds</h1>
          <p className="text-xl text-slate-300">Access 733,000+ verified carriers nationwide</p>

          <div className="flex justify-center gap-8 mt-8">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              <span>Free to Post</span>
            </div>
            <div className="flex items-center gap-2 text-green-400">
              <Zap className="w-5 h-5" />
              <span>Fast Matching</span>
            </div>
            <div className="flex items-center gap-2 text-green-400">
              <Shield className="w-5 h-5" />
              <span>Vetted Carriers</span>
            </div>
          </div>

          <div
            data-testid="load-post-not-broker-banner"
            className="mt-6 mx-auto max-w-2xl text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-2 leading-relaxed"
          >
            <strong>Tempus Dispatch is a SaaS platform — not a freight broker.</strong> Your load is
            shared with carriers who contract directly with you. We don't take freight payments or arrange transportation.
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          {[
            { num: 1, label: 'Your Info' },
            { num: 2, label: 'Pickup' },
            { num: 3, label: 'Delivery' },
            { num: 4, label: 'Load Details' }
          ].map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s.num ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'
              }`}>
                {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
              </div>
              <span className={`ml-2 text-sm ${step >= s.num ? 'text-white' : 'text-slate-500'}`}>{s.label}</span>
              {i < 3 && <ArrowRight className="w-4 h-4 text-slate-600 mx-4" />}
            </div>
          ))}
        </div>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8">
            
            {/* Step 1: Broker Info */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <Building2 className="w-8 h-8 text-blue-400" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">Your Information</h2>
                    <p className="text-slate-400">Tell us about your company</p>
                  </div>
                </div>
                
                {/* User Type Selection */}
                <div className="mb-6">
                  <Label className="text-slate-300 mb-3 block">I am a... *</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 'broker', label: 'Freight Broker', icon: '🤝' },
                      { value: 'shipper', label: 'Direct Shipper', icon: '🏭' },
                      { value: 'carrier', label: 'Carrier', icon: '🚛' }
                    ].map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setUserType(type.value)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          userType === type.value 
                            ? 'border-blue-500 bg-blue-500/20 text-white' 
                            : 'border-slate-600 bg-slate-800 text-slate-400 hover:border-slate-500'
                        }`}
                      >
                        <span className="text-2xl block mb-1">{type.icon}</span>
                        <span className="text-sm font-medium">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-slate-300">Company Name *</Label>
                    <Input name="company_name" value={formData.company_name} onChange={handleChange} placeholder="ABC Logistics Inc" className="bg-slate-900 border-slate-600" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Contact Name</Label>
                    <Input name="contact_name" value={formData.contact_name} onChange={handleChange} placeholder="John Smith" className="bg-slate-900 border-slate-600" />
                  </div>
                  <div>
                    <Label className="text-slate-300">
                      MC Number {userType !== 'shipper' ? '*' : '(Optional)'}
                    </Label>
                    <Input 
                      name="mc_number" 
                      value={formData.mc_number} 
                      onChange={handleChange} 
                      placeholder="MC123456" 
                      className="bg-slate-900 border-slate-600" 
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {userType === 'shipper' 
                        ? 'Optional for direct shippers' 
                        : 'Required for brokers & carriers'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-300">Email *</Label>
                    <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="john@abclogistics.com" className="bg-slate-900 border-slate-600" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Phone *</Label>
                    <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="(555) 123-4567" className="bg-slate-900 border-slate-600" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Pickup */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <MapPin className="w-8 h-8 text-green-400" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">Pickup Location</h2>
                    <p className="text-slate-400">Where should the carrier pick up?</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label className="text-slate-300">City *</Label>
                    <Input name="pickup_city" value={formData.pickup_city} onChange={handleChange} placeholder="Los Angeles" className="bg-slate-900 border-slate-600" />
                  </div>
                  <div>
                    <Label className="text-slate-300">State *</Label>
                    <Input name="pickup_state" value={formData.pickup_state} onChange={handleChange} placeholder="CA" maxLength={2} className="bg-slate-900 border-slate-600" />
                  </div>
                  <div>
                    <Label className="text-slate-300">ZIP Code</Label>
                    <Input name="pickup_zip" value={formData.pickup_zip} onChange={handleChange} placeholder="90001" className="bg-slate-900 border-slate-600" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-300">Pickup Date *</Label>
                    <Input name="pickup_date" type="date" value={formData.pickup_date} onChange={handleChange} className="bg-slate-900 border-slate-600" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Delivery */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <Package className="w-8 h-8 text-orange-400" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">Delivery Location</h2>
                    <p className="text-slate-400">Where is the freight going?</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label className="text-slate-300">City *</Label>
                    <Input name="delivery_city" value={formData.delivery_city} onChange={handleChange} placeholder="Dallas" className="bg-slate-900 border-slate-600" />
                  </div>
                  <div>
                    <Label className="text-slate-300">State *</Label>
                    <Input name="delivery_state" value={formData.delivery_state} onChange={handleChange} placeholder="TX" maxLength={2} className="bg-slate-900 border-slate-600" />
                  </div>
                  <div>
                    <Label className="text-slate-300">ZIP Code</Label>
                    <Input name="delivery_zip" value={formData.delivery_zip} onChange={handleChange} placeholder="75001" className="bg-slate-900 border-slate-600" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-300">Delivery Date</Label>
                    <Input name="delivery_date" type="date" value={formData.delivery_date} onChange={handleChange} className="bg-slate-900 border-slate-600" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Load Details */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <Truck className="w-8 h-8 text-purple-400" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">Load Details</h2>
                    <p className="text-slate-400">Specify equipment and rate</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Equipment Type</Label>
                    <select name="equipment_type" value={formData.equipment_type} onChange={handleChange} className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white">
                      <option value="sprinter_van">Sprinter Van</option>
                      <option value="cargo_van">Cargo Van</option>
                      <option value="box_truck">Box Truck</option>
                      <option value="hotshot">Hotshot</option>
                      <option value="dry_van">Dry Van</option>
                      <option value="reefer">Reefer</option>
                      <option value="flatbed">Flatbed</option>
                      <option value="step_deck">Step Deck</option>
                      <option value="conestoga">Conestoga</option>
                      <option value="lowboy">Lowboy</option>
                      <option value="rgn">RGN (Removable Gooseneck)</option>
                      <option value="double_drop">Double Drop</option>
                      <option value="curtain_side">Curtain Side</option>
                      <option value="tanker">Tanker</option>
                      <option value="hopper">Hopper</option>
                      <option value="dump_truck">Dump Truck</option>
                      <option value="power_only">Power Only</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Weight (lbs)</Label>
                    <Input name="weight_lbs" type="number" value={formData.weight_lbs} onChange={handleChange} placeholder="42000" className="bg-slate-900 border-slate-600" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-300">Commodity</Label>
                    <Input name="commodity" value={formData.commodity} onChange={handleChange} placeholder="Steel beams, machinery, etc." className="bg-slate-900 border-slate-600" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Your Rate</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input name="rate_amount" type="number" value={formData.rate_amount} onChange={handleChange} placeholder="4500" className="pl-9 bg-slate-900 border-slate-600" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300">Rate Type</Label>
                    <select name="rate_type" value={formData.rate_type} onChange={handleChange} className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white">
                      <option value="flat">Flat Rate</option>
                      <option value="per_mile">Per Mile</option>
                      <option value="open">Open to Offers</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-300">Special Requirements</Label>
                    <Input name="special_requirements" value={formData.special_requirements} onChange={handleChange} placeholder="Tarps required, team drivers, etc." className="bg-slate-900 border-slate-600" />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-700">
              {step > 1 ? (
                <Button variant="outline" onClick={() => setStep(step - 1)}>
                  ← Back
                </Button>
              ) : (
                <div></div>
              )}
              
              {step < 4 ? (
                <Button onClick={nextStep} className="bg-blue-600 hover:bg-blue-700">
                  Continue →
                </Button>
              ) : (
                <Button 
                  onClick={submitLoad} 
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700 px-8"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Posting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Post Load
                    </span>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Trust Indicators */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          <p>Trusted by 500+ brokers and shippers nationwide</p>
          <p className="mt-2">🔒 Your information is secure and never shared without permission</p>
        </div>
      </div>
      <PlatformDisclaimer />
    </div>
  );
};

export default LoadPostingPortal;
